import React, { useEffect, useState, useCallback } from "react"
import axios from "axios"

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"

const brl = (v: any, d = 2) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d })}`
const dataBR = (iso: string | null) => (iso ? iso.split("-").reverse().join("/") : "—")
const erroMsg = (e: any) => e?.response?.data?.erro || "Erro ao falar com o servidor"
const num = (v: any) => Number(v || 0)

const NOMES_FUNCAO: Record<string, string> = {
  comprovante_abastecimento: "Leitura de comprovantes de abastecimento",
  agente_chat: "Assistente de IA",
  despesa_viagem: "Despesas de viagem",
}
const nomeFuncao = (f: string) => NOMES_FUNCAO[f] || f.replace(/_/g, " ")

const th = { padding: "10px 14px", textAlign: "left" as const, fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase" as const }
const td = { padding: "10px 14px", fontSize: 12 }

export default function IAMeuUso() {
  const [resumo, setResumo] = useState<any>(null)
  const [usuarios, setUsuarios] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [semAcesso, setSemAcesso] = useState(false)
  const [salvandoId, setSalvandoId] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ia/meu/resumo`)
      setResumo(r.data)
      if (r.data?.ativo) {
        const u = await axios.get(`${API}/ia/meu/usuarios`)
        setUsuarios(u.data)
      }
    } catch (e: any) {
      if (e?.response?.status === 403) setSemAcesso(true)
      else alert(erroMsg(e))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { carregar() }, [carregar])

  const alternar = async (u: any) => {
    if (salvandoId) return
    const novo = !u.ia_liberado
    setSalvandoId(u.id)
    setUsuarios((lista) => lista.map((x) => (x.id === u.id ? { ...x, ia_liberado: novo } : x)))
    try {
      await axios.put(`${API}/ia/meu/usuarios/${u.id}`, { liberado: novo })
    } catch (e: any) {
      setUsuarios((lista) => lista.map((x) => (x.id === u.id ? { ...x, ia_liberado: !novo } : x)))
      alert(erroMsg(e))
    } finally { setSalvandoId(null) }
  }

  const cabecalho = (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700 }}>🤖 Inteligência Artificial — Meu uso</h2>
      <p style={{ fontSize: 13, color: "#64748b" }}>Acompanhe o saldo da sua empresa e escolha quem pode usar o assistente de IA</p>
    </div>
  )

  if (loading) return <div style={{ padding: 24 }}>{cabecalho}<div style={{ color: "#64748b" }}>Carregando...</div></div>

  if (semAcesso) return (
    <div style={{ padding: 24 }}>{cabecalho}
      <div className="card" style={{ padding: 20, color: "#94a3b8", fontSize: 13 }}>
        Esta tela é do <b>administrador da empresa</b>. Para configurar a IA de um cliente, use <b>Inteligência Artificial → IA — Clientes</b>;
        para ver o que o cliente vê, use "Acessar como" um administrador dele.
      </div>
    </div>
  )

  if (!resumo?.ativo) return (
    <div style={{ padding: 24 }}>{cabecalho}
      <div className="card" style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>O módulo de IA não está ativo na sua empresa</div>
        <div style={{ fontSize: 12, color: "#64748b" }}>Para contratar ou saber mais, fale com a MoobiFleet.</div>
      </div>
    </div>
  )

  const pct = resumo.percentual_usado
  const esgotado = pct != null && pct >= 100
  const corBarra = pct == null ? "#475569" : pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#22c55e"
  const cards = [
    { label: "Pacote do mês", valor: brl(resumo.credito_brl), cor: "#0ea5e9", icone: "📦" },
    { label: "Usado no mês", valor: brl(resumo.consumo_brl, 4), cor: "#f1f5f9", icone: "📊" },
    { label: "Saldo restante", valor: brl(resumo.restante_brl, 4), cor: "#22c55e", icone: "✅" },
    { label: "Excedente (vai na fatura)", valor: brl(resumo.excedente_brl, 4), cor: num(resumo.excedente_brl) > 0 ? "#f59e0b" : "#64748b", icone: "📈" },
    { label: "Leituras no mês", valor: num(resumo.chamadas).toLocaleString("pt-BR"), cor: "#a78bfa", icone: "🔎" },
  ]
  const liberados = usuarios.filter((u) => u.ia_liberado).length

  return (
    <div style={{ padding: 24, height: "100%", overflowY: "auto" }}>
      {cabecalho}

      {resumo.em_teste && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 16, border: "1px solid #7c3aed", background: "#1e1040", color: "#c4b5fd", fontSize: 12 }}>
          🧪 <b>Período de teste até {dataBR(resumo.teste_ate)}</b>
          {resumo.teste_limite_brl != null ? ` (limite de ${brl(resumo.teste_limite_brl)})` : ""} — o uso neste período <b>não é cobrado</b>.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 14, marginBottom: 18 }}>
        {cards.map((s) => (
          <div key={s.label} className="card" style={{ padding: "14px 16px" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icone}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: s.cor }}>{s.valor}</div>
            <div style={{ fontSize: 11, color: "#64748b" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {pct != null && (
        <div className="card" style={{ padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
            <span style={{ color: "#94a3b8" }}>Uso do pacote</span>
            <span style={{ color: corBarra, fontWeight: 700 }}>{pct}%</span>
          </div>
          <div style={{ height: 8, background: "#1e2f4a", borderRadius: 4 }}>
            <div style={{ height: 8, width: `${Math.min(100, pct)}%`, background: corBarra, borderRadius: 4 }} />
          </div>
          {pct >= 80 && !esgotado && (
            <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 8 }}>⚠️ Você já usou {pct}% do pacote deste mês.</div>
          )}
          {esgotado && (
            <div style={{ fontSize: 11, color: "#f87171", marginTop: 8 }}>
              {resumo.permite_excedente
                ? "O pacote acabou. A IA continua funcionando e o excedente entra na sua fatura."
                : "O pacote acabou. A IA fica indisponível até o dia 1º do mês seguinte."}
            </div>
          )}
          <div style={{ fontSize: 10, color: "#475569", marginTop: 8 }}>O pacote renova todo dia 1º às 00:01 (horário de Belém).</div>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8", marginBottom: 8 }}>Uso por função neste mês</div>
      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#0d1b2e" }}>{["Função", "Leituras", "Valor"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {(resumo.por_funcao || []).length === 0 ? (
              <tr><td colSpan={3} style={{ padding: 18, textAlign: "center", color: "#64748b" }}>Nenhum uso da IA neste mês ainda</td></tr>
            ) : resumo.por_funcao.map((f: any) => (
              <tr key={f.funcao} style={{ borderBottom: "1px solid rgba(45,65,102,0.4)" }}>
                <td style={td}>{nomeFuncao(f.funcao)}</td>
                <td style={td}>{num(f.chamadas)}</td>
                <td style={td}>
                  {num(f.valor_brl) > 0 && <span style={{ color: "#22c55e", fontWeight: 600 }}>{brl(f.valor_brl, 4)}</span>}
                  {num(f.valor_teste_brl) > 0 && <span style={{ color: "#c4b5fd", marginLeft: num(f.valor_brl) > 0 ? 8 : 0 }}>{brl(f.valor_teste_brl, 4)} no teste (não cobrado)</span>}
                  {num(f.valor_brl) === 0 && num(f.valor_teste_brl) === 0 && <span style={{ color: "#475569" }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>Quem pode usar o assistente de IA</div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
        A <b>leitura de comprovantes de abastecimento</b> funciona para a empresa toda, sem precisar de liberação. A liberação abaixo vale para o
        <b> assistente de IA</b>. Cada uso tem custo, então libere só quem precisa. {liberados} de {usuarios.length} usuário(s) liberado(s).
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#0d1b2e" }}>{["Usuário", "Perfil", "Situação", "Pode usar a IA"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {usuarios.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 18, textAlign: "center", color: "#64748b" }}>Nenhum usuário</td></tr>
            ) : usuarios.map((u) => (
              <tr key={u.id} style={{ borderBottom: "1px solid rgba(45,65,102,0.4)", opacity: u.ativo ? 1 : 0.55 }}>
                <td style={td}><div style={{ fontWeight: 600, fontSize: 13 }}>{u.nome}</div><div style={{ fontSize: 10, color: "#64748b" }}>{u.email}</div></td>
                <td style={{ ...td, color: "#94a3b8" }}>{u.perfil}</td>
                <td style={td}>
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: u.ativo ? "#14532d" : "#1e293b", color: u.ativo ? "#4ade80" : "#64748b" }}>
                    {u.ativo ? "ativo" : "inativo"}
                  </span>
                </td>
                <td style={td}>
                  <button type="button" role="switch" aria-checked={!!u.ia_liberado} aria-label={`Liberar IA para ${u.nome}`}
                    disabled={salvandoId === u.id} onClick={() => alternar(u)}
                    style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", position: "relative",
                      background: u.ia_liberado ? "#22c55e" : "#334155", transition: "background .15s" }}>
                    <span style={{ position: "absolute", top: 3, left: u.ia_liberado ? 23 : 3, width: 18, height: 18, borderRadius: 9, background: "#fff", transition: "left .15s" }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
